import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DefaultDataService, HttpUrlGenerator } from '@ngrx/data';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiResponse } from '@core/models/api-response.model';
import { DailyCheckInCurrency, DailyCheckInSet, DailyCheckInSettings } from '@core/models/daily-check-in.model';

/**
 * Daily Check-In backoffice API — AIO-80387.
 *
 * Two surfaces on one page: the global settings singleton, and the reward-set collection.
 * A set is saved whole (member groups + all 7 days + every currency) in a single request, so
 * the server can commit it in one transaction.
 */
@Injectable()
export class DailyCheckInDataService extends DefaultDataService<DailyCheckInSet> {

  messages$ = new Subject<any[]>();

  constructor(http: HttpClient, httpUrlGenerator: HttpUrlGenerator) {
    super('DailyCheckInSet', http, httpUrlGenerator, { root: '' });
  }

  // ----- basic settings panel -------------------------------------------------------------

  /**
   * Returns the settings row AND the global active-currency list (with symbols) in one call —
   * the grid needs both, and the currency list must match what validation requires.
   */
  getSettings(): Observable<{ settings: DailyCheckInSettings; currencies: DailyCheckInCurrency[] }> {
    return this.http.get<ApiResponse>('/dailycheckin/settings').pipe(
      map(res => ({ settings: res.data.rows, currencies: res.data.currencies || [] }))
    );
  }

  updateSettings(settings: DailyCheckInSettings): Observable<DailyCheckInSettings> {
    return this.http.put<ApiResponse>('/dailycheckin/settings', settings).pipe(
      tap(res => this.messages$.next(res.message)),
      map(res => res.data.rows)
    );
  }

  /**
   * Promotions for the Promo day-cell dropdown.
   *
   * Reuses the shared /promotiondropdown endpoint, which returns {id, name, code} and does
   * NOT filter by status or soft-delete. The API rejects an inactive or trashed promotion on
   * save, so a stale pick surfaces as a validation error rather than a bad schedule. Filtering
   * it properly would mean changing Promotion::getDropdownData, which every other BO dropdown
   * shares — out of scope here.
   */
  getPromotions(): Observable<any[]> {
    // Not /promotiondropdown: that one returns id/name/code only. This endpoint tags each
    // promotion with the currencies and member groups it targets, which is what lets the grid
    // narrow the picker per cell.
    return this.http.get<ApiResponse>('/dailycheckin/promotions').pipe(
      map(res => res.data.rows)
    );
  }

  // ----- reward sets ----------------------------------------------------------------------

  getAll(): Observable<DailyCheckInSet[]> {
    return this.http.get<ApiResponse>('/dailycheckin/sets').pipe(
      map(res => res.data.rows)
    );
  }

  getWithQuery(pageParam: string): Observable<DailyCheckInSet[]> {
    return this.http.get<ApiResponse>(`/dailycheckin/sets${pageParam}`).pipe(
      map(res => res.data.rows)
    );
  }

  /**
   * `content`, not `rows` — deliberate, and verified against the endpoint.
   * DailyCheckInController::store() returns `data.content` while update() and index() return
   * `data.rows`, mirroring LuckyDrawSettingController, the sibling this page was modelled on.
   * It is the minority shape (3 of 68 admin controllers), so it reads like a typo; it is not.
   * Changing it means changing the controller in the same PR.
   *
   * The success tap only fires when the payload is usable. That is belt-and-braces today rather
   * than a live fix: `content` cannot be null on a 200, because store() returns createSet() and
   * both createSet() and loadSet() declare a non-nullable `: DailyCheckInSet`, so a null raises a
   * TypeError and surfaces as a 500 — the error-interceptor path, where this tap never runs.
   * The guard keeps the banner honest if that return type is ever relaxed, since the failure it
   * would cause is silent: the set keeps id === null, stays dirty, and a retry POSTs a duplicate.
   *
   * Note the "HTTP 200 with an empty body" case on the component belongs to the UPLOAD endpoint
   * (/file returning []), which has nothing constraining its shape. This endpoint is not that.
   */
  add(set: DailyCheckInSet): Observable<DailyCheckInSet> {
    return this.http.post<ApiResponse>('/dailycheckin/sets', set).pipe(
      tap(res => {
        if (res.data && res.data.content) {
          this.messages$.next(res.message);
        }
      }),
      map(res => res.data.content)
    );
  }

  /** `rows` here — PUT and GET use that shape. See the note on add() for why POST differs. */
  updateSet(set: DailyCheckInSet): Observable<DailyCheckInSet> {
    return this.http.put<ApiResponse>(`/dailycheckin/sets/${set.id}`, set).pipe(
      tap(res => {
        if (res.data && res.data.rows) {
          this.messages$.next(res.message);
        }
      }),
      map(res => res.data.rows)
    );
  }

  deleteSet(id: number): Observable<any> {
    return this.http.delete<ApiResponse>(`/dailycheckin/sets/${id}`).pipe(
      tap(res => this.messages$.next(res.message))
    );
  }
}
